import { defineMessages } from "./define.js";

/** Обсуждения: the general chat, the threads, and the composer (SPEC §4.5). */
export const chat = defineMessages("chat", {
  title: { ru: "Обсуждения", en: "Discussions" },
  viewTabs: { ru: "Вид обсуждений", en: "Discussion view" },

  /** Also the general topic's own title — the server stores none for it. */
  general: { ru: "Общий чат", en: "General chat" },
  threads: { ru: "Темы", en: "Threads" },
  threadsCount: { ru: "Темы · {count}", en: "Threads · {count}" },

  backToThreads: { ru: "Ко всем темам", en: "All threads" },
  threadFallback: { ru: "Тема", en: "Thread" },
  statusOpen: { ru: "открыта", en: "open" },
  statusResolved: { ru: "решена", en: "resolved" },
  reopen: { ru: "Открыть снова", en: "Reopen" },
  resolve: { ru: "Решено", en: "Resolve" },

  /**
   * Russian needs all three forms: 1 сообщение, 2 сообщения, 5 сообщений — and
   * 11 to 14 go with the last one, which is what the hand-rolled helper this
   * replaces existed to remember.
   */
  messageCount: {
    ru: {
      one: "{count} сообщение",
      few: "{count} сообщения",
      many: "{count} сообщений",
      other: "{count} сообщений",
    },
    en: { one: "{count} message", other: "{count} messages" },
  },

  threadsEmpty: {
    ru: "Тем пока нет. Заводите тему, когда вопрос не помещается в один разговор.",
    en: "No threads yet. Start one when a question outgrows the chat.",
  },
  threadsLoading: { ru: "Загружаем темы…", en: "Loading threads…" },
  resolvedHeading: { ru: "Решённые", en: "Resolved" },

  newThreadPlaceholder: { ru: "О чём тема", en: "What's it about" },
  newThreadLabel: { ru: "Название темы", en: "Thread title" },
  newThreadSubmit: { ru: "Завести", en: "Start" },

  /*
   * Pinning. Threads are made by discussing wishes, items and events, so the
   * list grows on its own — these are the words for holding a few of them at the
   * top of it, and the pin is shared, not private to one device.
   */
  pinnedHeading: { ru: "Закреплённые", en: "Pinned" },
  pin: { ru: "Закрепить «{title}»", en: "Pin “{title}”" },
  unpin: { ru: "Открепить «{title}»", en: "Unpin “{title}”" },
  movePinned: { ru: "Переместить «{title}»", en: "Move “{title}”" },
  moveUp: { ru: "Выше: «{title}»", en: "Move “{title}” up" },
  moveDown: { ru: "Ниже: «{title}»", en: "Move “{title}” down" },

  noReplies: { ru: "Пока без ответов", en: "No replies yet" },
  toNewest: { ru: "К последнему сообщению", en: "Jump to the newest message" },

  loading: { ru: "Загружаем…", en: "Loading…" },
  noGeneral: {
    ru: "Общий чат для этого пространства ещё не создан.",
    en: "This space has no general chat yet.",
  },
  generalEmpty: {
    ru: "Здесь пока тихо. Первое сообщение появится у обоих.",
    en: "Quiet so far. The first message shows up for both of you.",
  },
  threadEmpty: {
    ru: "В теме пока нет ни одного сообщения.",
    en: "Nothing has been said in this thread yet.",
  },

  edited: { ru: "изменено", en: "edited" },
  reply: { ru: "Ответить", en: "Reply" },

  editing: { ru: "Редактирование", en: "Editing" },
  replyingTo: { ru: "Ответ · {name}", en: "Reply · {name}" },
  cancelEditing: { ru: "Отменить редактирование", en: "Cancel editing" },
  cancelReply: { ru: "Отменить ответ", en: "Cancel reply" },
  editMessage: { ru: "Изменить сообщение", en: "Edit message" },
  messagePlaceholder: { ru: "Сообщение", en: "Message" },
  newMessage: { ru: "Новое сообщение", en: "New message" },
  send: { ru: "Отправить", en: "Send" },
  // The files hang off a message, so a message that never landed takes them
  // with it. Said on the previews themselves, which are still on screen.
  sendFailed: {
    ru: "Сообщение не ушло — файл остался здесь",
    en: "The message didn't go — the file stayed here",
  },

  /*
   * Реакции. Лицо в подпись не подставляется словом: эмодзи и есть эмодзи, и
   * называть его «сердце» пришлось бы на двух языках и для шести штук — а
   * скринридер прочитает символ сам.
   */
  react: { ru: "Отреагировать {emoji}", en: "React with {emoji}" },
  reactionYours: { ru: "Ваша реакция {emoji}, снять", en: "Your {emoji}, take it back" },
  reactionTheirs: { ru: "{name}: {emoji}", en: "{name}: {emoji}" },

  /* Темы заводятся не только руками: каждое обсуждение хотелки, дела или
     события — тоже тема. Промахнувшийся по «Обсудить» не должен остаться с
     ней навсегда. Общий чат не удаляется — это вся история пространства. */
  removeThread: { ru: "Удалить тему «{title}»", en: "Delete the thread “{title}”" },
  removeThreadTitle: { ru: "Удалить «{title}»?", en: "Delete “{title}”?" },
  removeThreadWhat: {
    ru: {
      one: "Вместе с темой исчезнет {count} сообщение.",
      few: "Вместе с темой исчезнут {count} сообщения.",
      many: "Вместе с темой исчезнут {count} сообщений.",
      other: "Вместе с темой исчезнут {count} сообщения.",
    },
    en: {
      one: "{count} message goes with it.",
      other: "{count} messages go with it.",
    },
  },
  removeThreadBoth: { ru: "У обоих и без возврата.", en: "For both of you, with no way back." },

  /* Счётчик нового. Число, а не точка: «одно» и «двадцать одно» — разные утра. */
  unreadHere: {
    ru: {
      one: "{count} новое сообщение",
      few: "{count} новых сообщения",
      many: "{count} новых сообщений",
      other: "{count} новых сообщения",
    },
    en: { one: "{count} new message", other: "{count} new messages" },
  },

  untitled: { ru: "Без названия", en: "Untitled" },
  someone: { ru: "Кто-то", en: "Someone" },
  /** Стоит вместо текста там, где вложение показать негде: список тем, цитата. */
  wordless: { ru: "Файл", en: "A file" },
});
